import React, { useEffect, useRef, useState } from 'react';
import './TableInfoDialog.css';

interface TableInfoDialogProps {
    tables: { id: number; tableName: string; nfcUrl: string | null }[];
    storeId: string;
    onClose: () => void;
    onDeleteTable: (tableId: number) => Promise<void>;
    canDeleteTable: (tableId: number) => boolean;
    pendingTables: Record<number, boolean>;
}

const TableInfoDialog: React.FC<TableInfoDialogProps> = ({ tables, storeId, onClose, onDeleteTable, canDeleteTable, pendingTables }) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [qrTableId, setQrTableId] = useState<number | null>(null);
    const [copyMessage, setCopyMessage] = useState('');

    useEffect(() => {
        const dialog = dialogRef.current;
        dialog?.showModal();
        return () => dialog?.close();
    }, []);

    const copyUrl = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopyMessage('URL을 복사했습니다.');
        } catch {
            setCopyMessage('복사하지 못했습니다. 표시된 URL을 직접 복사해 주세요.');
        }
    };

    return (
        <dialog
            ref={dialogRef}
            className="table-info-dialog"
            aria-labelledby="table-info-title"
            onClose={() => {
                if (!dialogRef.current?.open) onClose();
            }}
            onClick={event => {
                if (event.target === event.currentTarget) dialogRef.current?.close();
            }}
        >
            <div className="table-info-content">
                <header className="table-info-header">
                    <div>
                        <h2 id="table-info-title">테이블 정보</h2>
                        <p>테이블별 주문 페이지와 QR코드를 확인하세요.</p>
                    </div>
                    <button type="button" autoFocus onClick={() => dialogRef.current?.close()} aria-label="테이블 정보 닫기">
                        닫기
                    </button>
                </header>
                {tables.length === 0 ? (
                    <p>등록된 테이블이 없습니다.</p>
                ) : (
                    <div className="table-info-grid">
                        {tables.map(table => {
                            const orderUrl = table.nfcUrl || `${window.location.origin}/order/store/${storeId}/table/${table.id}`;
                            const showQr = qrTableId === table.id;

                            return (
                                <section key={table.id} className="table-info-card" aria-label={`${table.tableName} 정보`}>
                                    <h3>{table.tableName}</h3>
                                    <div className="table-info-actions">
                                        <a href={orderUrl} target="_blank" rel="noopener noreferrer">주문 페이지</a>
                                        <button
                                            type="button"
                                            aria-expanded={showQr}
                                            aria-controls={`table-info-qr-${table.id}`}
                                            onClick={() => {
                                                setQrTableId(showQr ? null : table.id);
                                                setCopyMessage('');
                                            }}
                                        >
                                            {showQr ? 'QR코드 닫기' : 'QR코드 보기'}
                                        </button>
                                    </div>
                                    <div id={`table-info-qr-${table.id}`} hidden={!showQr}>
                                        {showQr && (
                                            <div className="table-info-qr">
                                                <img
                                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(orderUrl)}`}
                                                    alt={`${table.tableName} 주문 QR코드`}
                                                    width={240}
                                                    height={240}
                                                />
                                                <p className="table-info-url">{orderUrl}</p>
                                                <button type="button" onClick={() => copyUrl(orderUrl)}>URL 복사</button>
                                                <p role="status">{copyMessage}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="table-info-delete">
                                        <button type="button" className="table-info-delete-button"
                                            disabled={!canDeleteTable(table.id)}
                                            onClick={() => onDeleteTable(table.id)}
                                            title="미정산 주문이 없을 때 삭제할 수 있습니다.">
                                            {pendingTables[table.id] ? '처리 중...' : '테이블 삭제'}
                                        </button>
                                        <p>미정산 주문이 없는 테이블만 삭제할 수 있습니다.</p>
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                )}
            </div>
        </dialog>
    );
};

export default TableInfoDialog;
